import React from 'react';
import {
  Building2,
  CalendarClock,
  Check,
  CreditCard,
  Edit3,
  Factory,
  FileText,
  Landmark,
  ListOrdered,
  PackagePlus,
  Send,
  ShoppingCart,
  Truck,
  UserCheck,
  UserCircle,
  Warehouse,
  X,
  XCircle
} from 'lucide-react';
import { PurchaseOrder } from '../../../../../types/PurchaseOrder.types';
import { GoodsReceipt } from '../../../../../types/GoodsReceipt.types';
import { Skeleton } from '../../../../../components/shared/Skeleton/Skeleton';
import { DetailPanel } from '../../../../../components/shared/DetailPanel/DetailPanel';
import {
  DetailCard,
  DetailField,
  DetailFieldList,
  DetailHeaderButton,
  DetailIdentity,
  DetailNotes
} from '../../../../../components/shared/DetailPanel/DetailBlocks';
import { PAYMENT_METHOD_LABELS } from '../../../../../constants/clients';
import { GOODS_RECEIPT_STATUS_LABELS } from '../../../../../constants/purchases';
import { PurchaseOrderStatusBadge } from './PurchaseOrderStatusBadge';
import { StatusTimeline } from './StatusTimeline';
import { formatDate, formatDateTime, formatMoney, formatQuantity } from './format';

export type OrderAction = 'submit' | 'approve' | 'reject' | 'cancel' | null;

interface PurchaseOrderDetailProps {
  order: PurchaseOrder;
  receipts: GoodsReceipt[];
  receiptsLoading: boolean;
  canApprove: boolean;
  actionLoading: OrderAction;
  onEdit: () => void;
  onSubmitForApproval: () => void;
  onApprove: () => void;
  onReject: () => void;
  onRegisterReceipt: () => void;
  onCancel: () => void;
  onSelectReceipt: (receipt: GoodsReceipt) => void;
  onClose: () => void;
}

// Campos que la ficha pinta siempre: en el esqueleto se muestran sus etiquetas
// y solo los valores van en skeleton.
const ALWAYS_SHOWN_FIELDS: { icon: React.ElementType; label: string }[] = [
  { icon: Building2, label: 'SOCIEDAD_COMPRADORA' },
  { icon: Warehouse, label: 'ALMACÉN_DE_DESTINO' },
  { icon: CalendarClock, label: 'ENTREGA_PREVISTA' },
  { icon: Truck, label: 'ENVÍO' },
  { icon: UserCircle, label: 'CREADA_POR' },
  { icon: UserCheck, label: 'APROBADA_POR' },
  { icon: CreditCard, label: 'CONDICIONES_DE_PAGO' }
];

/** Ficha de la orden mientras llega de la API: la carcasa real con los valores en skeleton. */
export const PurchaseOrderDetailSkeleton = ({ onClose }: { onClose: () => void }) => (
  <DetailPanel
    drawer
    title="Ficha_De_Orden"
    onClose={onClose}
    headerActions={<DetailHeaderButton icon={Edit3} label="Editar" onClick={() => undefined} disabled />}
    cards={
      <>
        <DetailCard icon={ListOrdered} title="Líneas">
          <Skeleton width="100%" height="2.2em" />
          <Skeleton width="100%" height="2.2em" />
        </DetailCard>
      </>
    }
  >
    <DetailIdentity
      icon={ShoppingCart}
      reference={<Skeleton width="80px" height="8px" />}
      name={<Skeleton width="55%" height="18px" />}
    />
    <DetailFieldList>
      {ALWAYS_SHOWN_FIELDS.map(f => (
        <DetailField key={f.label} icon={f.icon} label={f.label}>
          <Skeleton width="40%" height="10px" />
        </DetailField>
      ))}
    </DetailFieldList>
  </DetailPanel>
);

export const PurchaseOrderDetail = ({
  order,
  receipts,
  receiptsLoading,
  canApprove,
  actionLoading,
  onEdit,
  onSubmitForApproval,
  onApprove,
  onReject,
  onRegisterReceipt,
  onCancel,
  onSelectReceipt,
  onClose
}: PurchaseOrderDetailProps) => {
  const busy = actionLoading !== null;
  const isDraft = order.status === 'draft';
  const isPending = order.status === 'pending_approval';
  const isReceivable = order.status === 'approved' || order.status === 'partially_received';
  const isClosed = order.status === 'received' || order.status === 'cancelled';

  const shipping = [order.incoterm, order.shippingMethod, order.carrier].filter(Boolean).join(' · ');
  const paymentTerms = order.paymentTermsSnapshot ? `${order.paymentTermsSnapshot} días` : '';
  const payment = [
    order.paymentMethodSnapshot ? (PAYMENT_METHOD_LABELS[order.paymentMethodSnapshot] || order.paymentMethodSnapshot) : '',
    paymentTerms
  ].filter(Boolean).join(' · ');

  return (
    <DetailPanel
      drawer
      title="Ficha_De_Orden"
      onClose={onClose}
      headerActions={
        <DetailHeaderButton
          icon={Edit3}
          label="Editar"
          onClick={onEdit}
          disabled={!isDraft || busy}
          title={isDraft ? undefined : 'Solo se pueden editar órdenes en borrador'}
        />
      }
      toolbar={
        /* Botonera por estado. Recibida y cancelada no admiten ninguna acción. */
        <div className="purchases-view__actions">
          {isDraft && (
            <button onClick={onSubmitForApproval} disabled={busy} className="module-form-submit-btn">
              <Send size={12} /> {actionLoading === 'submit' ? 'Enviando...' : 'Enviar a aprobación'}
            </button>
          )}
          {isPending && canApprove && (
            <>
              <button onClick={onApprove} disabled={busy} className="module-form-submit-btn">
                <Check size={12} /> {actionLoading === 'approve' ? 'Aprobando...' : 'Aprobar'}
              </button>
              <button onClick={onReject} disabled={busy} className="module-form-cancel-btn">
                <X size={12} /> Rechazar
              </button>
            </>
          )}
          {isPending && !canApprove && (
            <span className="purchases-view__actions-hint">
              Pendiente de la firma del supervisor de {order.createdByName || 'quien la solicitó'}
            </span>
          )}
          {isReceivable && (
            <button onClick={onRegisterReceipt} disabled={busy} className="module-form-submit-btn">
              <PackagePlus size={12} /> Registrar recepción
            </button>
          )}
          {!isClosed && (
            <button onClick={onCancel} disabled={busy} className="module-form-cancel-btn purchases-view__cancel-btn">
              <XCircle size={12} /> {actionLoading === 'cancel' ? 'Cancelando...' : 'Cancelar orden'}
            </button>
          )}
          {isClosed && (
            <span className="purchases-view__actions-hint">
              {order.status === 'received' ? 'Orden cerrada: mercancía recibida al completo' : 'Orden cancelada'}
            </span>
          )}
        </div>
      }
      cards={
        <>
          <DetailCard icon={ListOrdered} title={`Líneas (${order.lines.length})`}>
            <div className="purchases-view__lines-wrapper">
              <table className="purchases-view__lines-table purchases-view__lines-table--readonly">
                <thead>
                  <tr>
                    <th>Descripción</th>
                    <th className="right">Pedida</th>
                    <th className="right">Recibida</th>
                    <th className="right">Pendiente</th>
                    <th className="right">Precio</th>
                    <th className="right">Dto</th>
                    <th className="right">IVA</th>
                    <th className="right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {order.lines.map(line => {
                    const pending = Math.max(line.quantityOrdered - line.quantityReceived, 0);
                    return (
                      <tr key={line.id}>
                        <td>
                          <div className="purchases-view__line-desc">{line.description}</div>
                          <div className="purchases-view__line-sub">
                            {[line.bookTitle && line.bookTitle !== line.description ? line.bookTitle : '', line.sku, line.supplierReference ? `Ref. ${line.supplierReference}` : '']
                              .filter(Boolean).join(' · ') || (line.bookId ? 'Libro de catálogo' : 'Fuera de catálogo')}
                          </div>
                        </td>
                        <td className="right">{formatQuantity(line.quantityOrdered)} {line.unitOfMeasure}</td>
                        <td className="right">{formatQuantity(line.quantityReceived)}</td>
                        <td className={`right ${pending > 0 && !isDraft ? 'purchases-view__pending' : ''}`}>{formatQuantity(pending)}</td>
                        <td className="right">{formatMoney(line.unitPrice, order.currency)}</td>
                        <td className="right">{formatQuantity(line.discountPct)} %</td>
                        <td className="right">{formatQuantity(line.taxRate)} %</td>
                        <td className="right purchases-view__line-total">{formatMoney(line.lineTotal, order.currency)}</td>
                      </tr>
                    );
                  })}
                  {order.lines.length === 0 && (
                    <tr><td colSpan={8} className="purchases-view__lines-empty">La orden no tiene líneas todavía</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="purchases-view__lines-footer">
              <span />
              <div className="purchases-view__totals">
                <span>Base imponible <strong>{formatMoney(order.subtotal, order.currency)}</strong></span>
                <span>IVA <strong>{formatMoney(order.taxTotal, order.currency)}</strong></span>
                {order.shippingCost > 0 && <span>Portes <strong>{formatMoney(order.shippingCost, order.currency)}</strong></span>}
                <span className="purchases-view__totals-grand">Total <strong>{formatMoney(order.totalAmount, order.currency)}</strong></span>
              </div>
            </div>
          </DetailCard>

          <DetailCard icon={PackagePlus} title={`Recepciones ${receiptsLoading ? '' : `(${receipts.length})`}`}>
            {receiptsLoading ? (
              <>
                <Skeleton width="100%" height="2.2em" />
                <Skeleton width="100%" height="2.2em" />
              </>
            ) : receipts.length === 0 ? (
              <div className="purchases-view__empty-block">
                {isReceivable ? 'Todavía no se ha recibido mercancía de esta orden' : 'Sin recepciones'}
              </div>
            ) : (
              <div className="purchases-view__receipt-list">
                {receipts.map(r => (
                  <button key={r.id} type="button" onClick={() => onSelectReceipt(r)} className="purchases-view__receipt-item">
                    <div>
                      <span className="purchases-view__receipt-number">Albarán {r.deliveryNoteNumber || `#${r.id}`}</span>
                      <span className="purchases-view__receipt-meta">
                        {formatDateTime(r.receivedAt)}{r.packageCount !== null ? ` · ${r.packageCount} bultos` : ''}
                      </span>
                    </div>
                    <span className={`module-badge ${r.status === 'complete' ? 'completed' : 'pending'}`}>
                      {GOODS_RECEIPT_STATUS_LABELS[r.status]}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </DetailCard>

          <StatusTimeline order={order} />
        </>
      }
    >
      <DetailIdentity
        icon={ShoppingCart}
        reference={order.poNumber}
        name={order.supplierName || `Acreedor #${order.supplierId}`}
        subtitle={
          <>
            <PurchaseOrderStatusBadge status={order.status} />
            {' '}TOTAL: <span className="detail-identity-highlight">{formatMoney(order.totalAmount, order.currency)}</span>
          </>
        }
      />

      <DetailFieldList>
        <DetailField icon={Building2} label="SOCIEDAD_COMPRADORA">{order.companyName || `#${order.companyId}`}</DetailField>
        <DetailField icon={Warehouse} label="ALMACÉN_DE_DESTINO">{order.warehouseName || `#${order.warehouseId}`}</DetailField>
        <DetailField icon={CalendarClock} label="ENTREGA_PREVISTA">{formatDate(order.expectedDeliveryDate)}</DetailField>
        <DetailField icon={Truck} label="ENVÍO">{shipping || 'No declarado'}</DetailField>
        {order.alternateDeliveryAddress && (
          <DetailField icon={FileText} label="DIRECCIÓN_ALTERNATIVA">{order.alternateDeliveryAddress}</DetailField>
        )}
        <DetailField icon={UserCircle} label="CREADA_POR">{`${order.createdByName || '—'} · ${formatDateTime(order.createdAt)}`}</DetailField>
        <DetailField icon={UserCheck} label="APROBADA_POR">
          {order.approvedBy ? `${order.approvedByName || `#${order.approvedBy}`} · ${formatDateTime(order.issuedAt)}` : 'Pendiente'}
        </DetailField>
        <DetailField icon={CreditCard} label="CONDICIONES_DE_PAGO">{payment || 'No declaradas'}</DetailField>
        {order.bankAccountSnapshot && (
          <DetailField icon={Landmark} label="CUENTA_ACREEDOR">{order.bankAccountSnapshot}</DetailField>
        )}
        {order.currency !== 'EUR' && (
          <DetailField icon={Factory} label="MONEDA">{`${order.currency}${order.exchangeRate ? ` · tipo ${order.exchangeRate}` : ''}`}</DetailField>
        )}
      </DetailFieldList>

      {order.notes && <DetailNotes>{order.notes}</DetailNotes>}
    </DetailPanel>
  );
};
